import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, TextField } from '@heroui/react';
import { FormDrawer } from '@/components/formDrawer';
import { ImageCropUpload } from '@/components/imageCropUpload';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { useBrandForm } from './useBrandForm';

type BrandFormModalViewProps = {
    error: string | null;
    form: ReturnType<typeof useBrandForm>;
    isOpen: boolean;
    isSubmitting: boolean;
    mode: 'create' | 'edit';
    onClose: () => void;
};

const BRAND_IMAGE_CROP_SIZE = { width: 512, height: 512 };

export const BrandFormModalView: React.FC<BrandFormModalViewProps> = ({
    error,
    form,
    isOpen,
    isSubmitting,
    mode,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    const { image, isValid, name, onImageChange, onNameChange, onSubmit } =
        form;

    const copy = React.useMemo(
        () => ({
            title:
                mode === 'edit'
                    ? t('brands.components.brandTable.form.editTitle')
                    : t('brands.components.brandTable.form.title'),
            description: t('brands.components.brandTable.form.description'),
            imageLabel: t('brands.components.brandTable.form.imageLabel'),
            imageDescription: t(
                'brands.components.brandTable.form.imageDescription',
            ),
            imageSelect: t('brands.components.brandTable.form.imageSelect'),
            imageChange: t('brands.components.brandTable.form.imageChange'),
            imageRemove: t('brands.components.brandTable.form.imageRemove'),
            imageApply: t('brands.components.brandTable.form.imageApply'),
            imageUploadLabel: t(
                'brands.components.brandTable.form.imageUploadLabel',
            ),
            imageInvalidType: t(
                'brands.components.brandTable.form.imageInvalidType',
            ),
            imageInvalidSize: t(
                'brands.components.brandTable.form.imageInvalidSize',
            ),
            imageCropError: t(
                'brands.components.brandTable.form.imageCropError',
            ),
            imageReadError: t(
                'brands.components.brandTable.form.imageReadError',
            ),
            nameLabel: t('brands.components.brandTable.form.nameLabel'),
            namePlaceholder: t(
                'brands.components.brandTable.form.namePlaceholder',
            ),
            cancel: t('brands.components.brandTable.form.cancel'),
            submit:
                mode === 'edit'
                    ? t('brands.components.brandTable.form.editSubmit')
                    : t('brands.components.brandTable.form.submit'),
        }),
        [mode, t],
    );

    return (
        <FormDrawer
            footer={
                <>
                    <Button
                        isDisabled={isSubmitting}
                        onPress={onClose}
                        variant="tertiary"
                        {...getNodeTestIdProp('brand-form-cancel')}
                    >
                        {copy.cancel}
                    </Button>
                    <Button
                        isDisabled={!isValid}
                        isPending={isSubmitting}
                        onPress={onSubmit}
                        variant="primary"
                        {...getNodeTestIdProp('brand-form-submit')}
                    >
                        {copy.submit}
                    </Button>
                </>
            }
            isOpen={isOpen}
            onClose={onClose}
            subtitle={copy.description}
            testId="brand-form"
            title={copy.title}
        >
            <div
                className="flex flex-col gap-1.5"
                {...getNodeTestIdProp('brand-form-image-field')}
            >
                <Label
                    className="text-sm font-medium text-foreground"
                    {...getNodeTestIdProp('brand-form-image-label')}
                >
                    {copy.imageLabel}
                </Label>
                <ImageCropUpload
                    applyLabel={copy.imageApply}
                    aspectClassName="aspect-square"
                    boxClassName="mx-auto w-[150px]"
                    changeLabel={copy.imageChange}
                    cropErrorMessage={copy.imageCropError}
                    cropSize={BRAND_IMAGE_CROP_SIZE}
                    description={copy.imageDescription}
                    id="brand-form-image"
                    invalidSizeMessage={copy.imageInvalidSize}
                    invalidTypeMessage={copy.imageInvalidType}
                    label={copy.imageUploadLabel}
                    onChange={onImageChange}
                    readErrorMessage={copy.imageReadError}
                    removeLabel={copy.imageRemove}
                    selectLabel={copy.imageSelect}
                    value={image}
                />
            </div>
            <TextField
                isRequired
                onChange={onNameChange}
                value={name}
                {...getNodeTestIdProp('brand-form-name')}
            >
                <Label>{copy.nameLabel}</Label>
                <Input placeholder={copy.namePlaceholder} />
            </TextField>
            {error ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('brand-form-error')}
                >
                    {error}
                </p>
            ) : null}
        </FormDrawer>
    );
};
